import React from 'react';
import styled from 'styled-components';
import { IonButton, IonIcon } from '@ionic/react';
import { refreshOutline, closeOutline, warningOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';

interface UploadErrorSummaryProps {
  failedCount: number;
  errors?: string[];
  onRetryAll: () => void;
  onDismiss?: () => void;
  disabled?: boolean;
}

const Banner = styled.div`
  background: #fef5f5;
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    border-radius: 12px;
    padding: 16px 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const Message = styled.div`
  color: #991b1b;
  font-size: 15px;
  font-weight: 600;
  flex: 1;
  line-height: 1.5;
  min-width: 0;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const ErrorList = styled.div`
  padding: 12px 16px;
  background: rgba(254, 202, 202, 0.4);
  border-radius: 12px;
  margin-top: 0;

  @media (min-width: 768px) {
    padding: 12px 16px;
    border-radius: 8px;
  }
`;

const ErrorItem = styled.div`
  color: #7f1d1d;
  font-size: 13px;
  padding: 6px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.5;

  &:before {
    content: '•';
    flex-shrink: 0;
    font-weight: bold;
    color: #ef4444;
    font-size: 16px;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    font-size: 12px;
    padding: 5px 0;
  }
`;

const ToggleButton = styled(IonButton)`
  --padding-start: 0;
  --padding-end: 0;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  margin: 0;
  --border-radius: 20px;

  ion-icon {
    font-size: 20px;
  }

  @media (min-width: 768px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    --border-radius: 16px;

    ion-icon {
      font-size: 18px;
    }
  }
`;

const ActionButton = styled(IonButton)`
  --padding-start: 20px;
  --padding-end: 20px;
  height: 48px;
  min-height: 48px;
  margin: 0;
  --border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  text-transform: none;
  --box-shadow: 0 2px 8px rgba(94, 52, 178, 0.2);

  @media (min-width: 768px) {
    --padding-start: 18px;
    --padding-end: 18px;
    height: 44px;
    min-height: 44px;
    --border-radius: 10px;
    font-size: 14px;
  }
`;

const UploadErrorSummary: React.FC<UploadErrorSummaryProps> = ({
  failedCount,
  errors = [],
  onRetryAll,
  onDismiss,
  disabled,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (failedCount === 0) {
    return null;
  }

  const uniqueErrors = Array.from(new Set(errors.filter(Boolean)));
  const hasErrors = uniqueErrors.length > 0;

  return (
    <Banner>
      <Header>
        <HeaderContent>
          <IonIcon
            icon={warningOutline}
            style={{ fontSize: '24px', color: '#ef4444', flexShrink: 0 }}
          />
          <Message>
            {failedCount === 1
              ? '1 image failed to upload'
              : `${failedCount} images failed to upload`}
          </Message>
        </HeaderContent>
        <Actions>
          {hasErrors && (
            <ToggleButton
              size="small"
              fill="clear"
              color="medium"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <IonIcon icon={isExpanded ? chevronUpOutline : chevronDownOutline} />
            </ToggleButton>
          )}
          {onDismiss && (
            <ToggleButton size="small" fill="clear" color="medium" onClick={onDismiss}>
              <IonIcon icon={closeOutline} />
            </ToggleButton>
          )}
        </Actions>
      </Header>
      <ActionButton fill="solid" color="light" expand="block" onClick={onRetryAll} disabled={disabled}>
        <IonIcon icon={refreshOutline} slot="start" />
        Retry All Failed Images
      </ActionButton>
      {isExpanded && hasErrors && (
        <ErrorList>
          {uniqueErrors.map((error, index) => (
            <ErrorItem key={index}>{error}</ErrorItem>
          ))}
        </ErrorList>
      )}
    </Banner>
  );
};

export default UploadErrorSummary;
